/**
 * SearchResultRow — the one result row of the product search (refonte WP-37 / cross-module XWP-12;
 * design ux/04 §3.2). Rendered inside the foundations Combobox option (`renderOption`), so the
 * listbox semantics, the active row and the keys stay the Combobox's.
 *
 *   line 1 — name · dosage (matched tokens in bold) and the price the line is charged: the server's
 *            `prix_vente` (X XWP-77 / XWP-80, XD-228 — the FEFO head lot priced by the one resolver),
 *            « PPA produit … » under it when `Produit.ppa` differs, « Prix inconnu » when nothing
 *            prices it (never 0 DA, never `Produit.ppa` re-read here);
 *   line 2 — emplacement · laboratoire · DCI · forme (matched tokens in bold); on a counter row the
 *            place of the FEFO-first lot (X XWP-96, brief §4: « Magasin principal · AA-01 », the
 *            server's `emplacement.libelle`), else the product's shelves;
 *   tags   — the sellable figure of the availability layer (XWP-03: « Stock : n » / « Rupture »),
 *            the FEFO-first counter lot « Lot … · Pér. MM/AA » and « + n autres lots » (X XWP-96,
 *            brief §2 — the server's order), CNAS, next sellable expiry (MM/AA) without counter lots, the sale state when it is not sellable (Bloqué with
 *            its motif, En réserve — autre magasin, Rupture), Vente libre bloquée, Psychotrope, and
 *            the match explanation (« Code exact », « Orthographe proche », « trouvé par
 *            code-barres ») with the generics tier « Même DCI » (a hit by its DCI only);
 *   active — the counter actions of the row (« Signaler une rupture », « Vendre comme secours »)
 *            on the active row only, with their keys.
 */
import * as React from "react";
import { Highlight } from "@/components/ui/Combobox";
import { Badge } from "@/components/ui/primitives";
import { fmtMoney } from "@/lib/format.js";
import { ETAT_VENTE, moisAnnee, resumeLotsComptoir } from "@/lib/recherche-produit.js";
import { RECHERCHE_PRODUIT_COPIE as C } from "@/config/recherche-produit.messages.js";
import { PRIX_VENTE_COPIE as CP } from "@/config/prix-vente.messages.js";
import { prixDeLigneRecherche } from "@/lib/prix-vente.js";
import SecoursActions from "@/components/vente/SecoursActions.jsx";

const nomRef = (v) => (v && typeof v === "object" ? v.nom ?? v.libelle ?? null : v ?? null);

/**
 * @param {object} props
 * @param {object} props.option   an `optionPos` / `optionCatalogue` option (lib/recherche-produit.js)
 * @param {string} props.query
 * @param {boolean} props.active
 * @param {boolean} [props.pos]   counter row (availability tags and actions)
 * @param {boolean} [props.demandeSurRupture]  Entrée on a rupture row reports a demand
 * @param {(p: object) => void} [props.onRupture]
 * @param {(p: object) => void} [props.onSecours]
 * @param {{ motif?: string }|null} [props.secoursBloque]
 */
export function SearchResultRow({ option, query, active, pos = false, demandeSurRupture = false, onRupture, onSecours, secoursBloque = null }) {
  const p = option.produit ?? {};
  const etat = option.etat;
  const qte = option.qte ?? 0;
  const expl = option.explication ?? {};
  const meta = [
    nomRef(p.laboratoire) ?? p.laboratoire_nom,
    nomRef(p.dci),
    nomRef(p.forme),
  ].filter(Boolean);
  // X XWP-96 — the counter lots the server listed (a CAISSIER never receives a hidden one)
  const lots = pos && qte > 0 ? resumeLotsComptoir(p) : null;
  const emplacements = Array.isArray(p.emplacements) ? p.emplacements.map((e) => e?.code).filter(Boolean) : [];
  const per = pos && qte > 0 && !lots ? moisAnnee(p.prochaine_peremption) : null;
  const rupture = etat === ETAT_VENTE.RUPTURE;
  const actions = pos && active && etat !== ETAT_VENTE.BLOQUE && (onSecours || (rupture && onRupture));
  const prix = prixDeLigneRecherche(p);

  return (
    <span className="pk-srow">
      <span className="pk-srow-l1">
        <span className="pk-srow-nom">
          <Highlight text={option.label} query={query} />
        </span>
        <span className="pk-srow-prix" data-testid="srow-prix">
          {prix.montant != null ? (
            <span className="pk-srow-ppa">{fmtMoney(prix.montant)}</span>
          ) : (
            <span className="pk-srow-ppa pk-srow-ppa--inconnu">{CP.inconnu}</span>
          )}
          {prix.ppa_produit != null && <span className="pk-srow-ppa-produit">{CP.ppaProduit(fmtMoney(prix.ppa_produit))}</span>}
        </span>
      </span>
      {(meta.length > 0 || emplacements.length > 0 || lots?.emplacement) && (
        <span className="pk-srow-meta">
          {lots?.emplacement ? (
            <span className="pk-srow-empl" data-testid="srow-emplacement">
              {lots.emplacement}
              {meta.length > 0 ? " · " : ""}
            </span>
          ) : (
            emplacements.length > 0 && <span className="pk-srow-empl">{C.emplacement(emplacements.join(", "))} · </span>
          )}
          <Highlight text={meta.join(" · ")} query={query} />
        </span>
      )}
      <span className="pk-srow-tags">
        {pos && etat === ETAT_VENTE.BLOQUE && (
          <Badge tone="danger" size="sm" icon="ban">
            {C.bloque}
            {p.bloque_vente_libre_motif ? ` — ${p.bloque_vente_libre_motif}` : ""}
          </Badge>
        )}
        {pos && etat === ETAT_VENTE.RESERVE && (
          <Badge tone="warning" size="sm" icon="package">
            {C.enReserve}
          </Badge>
        )}
        {pos && rupture && (
          <Badge tone="danger" size="sm" icon="package-x">
            {demandeSurRupture ? C.ruptureDemande : C.ruptureIndisponible}
          </Badge>
        )}
        {pos && etat !== ETAT_VENTE.RUPTURE && etat !== ETAT_VENTE.BLOQUE && (
          <span className={qte > 0 ? "pk-srow-stock" : "pk-srow-stock pk-srow-stock--nul"}>{qte > 0 ? C.stock(qte) : C.rupture}</span>
        )}
        {lots && (
          <span className="pk-srow-lot" data-testid="srow-lot">
            {C.lotFefo(lots.numero_lot, lots.peremption)}
          </span>
        )}
        {lots?.receptions && (
          <span className="pk-srow-receptions" data-testid="srow-receptions" title="Même N° de lot, même péremption et même PPA : une seule ligne, la vente prend la réception la plus ancienne d'abord">
            {C.receptions(lots.receptions)}
          </span>
        )}
        {lots?.autres > 0 && (
          <span className="pk-srow-autres" data-testid="srow-autres-lots">
            {C.autresLots(lots.autres)}
          </span>
        )}
        {pos && p.is_remboursable && <Badge tone="info" size="sm" icon={false}>{C.cnas}</Badge>}
        {per && <span>{C.peremption(per)}</span>}
        {pos && p.bloque_vente_libre && (
          <Badge tone="danger" size="sm" icon="lock">
            {C.venteLibreBloquee}
          </Badge>
        )}
        {p.is_psychotrope && (
          <Badge tone="warning" size="sm" icon="alert-triangle">
            {C.psychotrope}
          </Badge>
        )}
        {expl.generique && <Badge tone="neutral" size="sm" icon={false}>{expl.generique}</Badge>}
        {expl.texte && <span className="pk-srow-expl">{expl.texte}</span>}
      </span>
      {actions && (
        <SecoursActions
          produit={p}
          onRupture={rupture ? onRupture : undefined}
          onSecours={onSecours}
          plafondAtteint={!!secoursBloque}
          motifBlocage={secoursBloque?.motif ?? null}
        />
      )}
    </span>
  );
}

export default SearchResultRow;
